import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useUrlParams from '../../hooks/useUrlParams';
import { Alert, App, Button, Empty, List, Modal, Space, Switch, Tag, Typography } from 'antd';

import AutomationRuleEditor from './AutomationRuleEditor';
import AutomationRunsModal from './AutomationRunsModal';
import CardActionEditor from './CardActionEditor';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import ExpectedItemRuleEditor from './ExpectedItemRuleEditor';
import GenerateItemsRuleEditor from './GenerateItemsRuleEditor';
import ThresholdRuleEditor from './ThresholdRuleEditor';
import WaitingRules, { TurnOnRuleModal } from './WaitingRules';
import {
	deleteAutomation,
	disableAutomation,
	enableAutomation,
	getAutomationRuns,
	getAutomations,
} from '../../services/automations';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { describeSchedule } from '../../utils/automationSchedule';
import { describeCardAction } from '../../utils/cardActionEditor';
import { ALWAYS_RUN_LABEL, ENTRY_EVENT, describeEntryTrigger } from '../../utils/entryRules';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useDateFormat from '../../hooks/useDateFormat';
import { PLUS } from '../icons';
import { confirmDestructive } from '../common/confirmDestructive';
import GroupHeader from '../common/GroupHeader';
import { managesParentOf, subCollectionsOf } from '../../utils/collectionFamily';
import useCollectionTags, { invalidateTagOwner } from '../../hooks/useCollectionTags';
import { tagsById as indexTags } from '../../utils/tags';

/**
 * Automations tab (managers only; the tab itself is gated by CollectionForm and
 * the API's automation routes are manage-gated besides).
 *
 * Like Sharing, everything here applies IMMEDIATELY through its own endpoints —
 * it lives inside the settings page but outside the Save-button lifecycle.
 *
 * Two kinds of row share this list on purpose. A `driven-field` row is MANAGED:
 * it was materialized server-side from an element's `options.drivenFrom`, so it
 * is view-only here and its edit affordance is a link back to the card that
 * owns it. A `rule` row is user-authored and fully editable. The one thing both
 * offer is the enabled Switch: pausing a noisy rule is a runtime decision, not a
 * re-authoring, and the API sanctions it for managed rules too.
 *
 * The last-run column is not decoration. The engine's cascade guards (write cap,
 * time budget, depth, notify cap) are recorded on the run row rather than
 * failing loudly, and the plan's rule is "no silent caps" — so a truncated run
 * surfaces as a warning Tag right where a manager is already looking.
 */

const { Text } = Typography;

const KIND_LABELS = {
	'driven-field': 'Driven field',
	rule: 'Rule',
	'expected-item': 'Expected item',
	'generate-items': 'Generator',
	threshold: 'Threshold',
	// A named button people press on a record (KPR-382, edited by KPR-550).
	'card-action': 'Action',
	// An automatic tag's rule (T6a): managed, authored on its tag.
	'tag-rule': 'Automatic tag',
};
const KIND_COLORS = {
	'driven-field': 'blue',
	rule: 'geekblue',
	'expected-item': 'purple',
	'generate-items': 'magenta',
	threshold: 'gold',
	'card-action': 'purple',
	'tag-rule': 'cyan',
};

const RUN_STATUS_COLORS = { succeeded: 'green', failed: 'red', skipped: 'default', pending: 'gold' };

// The engine's own vocabulary for "this run stopped early", turned into
// something a manager can act on. Anything unrecognized still shows, named.
const TRUNCATION_REASONS = {
	writes: 'the 500-item write cap was reached',
	timeBudget: 'the run ran out of time',
	depth: 'the cascade depth budget was reached',
	notify: 'the notification cap was reached',
	// A threshold sweep over an element bound cannot use an index, so it is
	// capped per rule per run — said out loud like every other cap.
	sweep: 'the sweep reached its per-run record cap',
};

/** Oxford-free list join: "a", "a or b", "a, b or c". */
const joinOr = words => {
	if ( words.length <= 1 ) return words[ 0 ] || '';
	return `${ words.slice( 0, -1 ).join( ', ' ) } or ${ words[ words.length - 1 ] }`;
};

const joinAnd = words => {
	if ( words.length <= 1 ) return words[ 0 ] || '';
	return `${ words.slice( 0, -1 ).join( ', ' ) } and ${ words[ words.length - 1 ] }`;
};

/**
 * What a GENERATOR makes, in a sentence: the records, what drives them, and
 * how many of them a day. The counterpart of describeCheck — a schedule rule's
 * cadence means nothing without the half that says what it is for.
 */
function describeGeneration( generate, cardsById ) {
	if ( ! generate ) return '';
	const sourceName = cardsById?.[ normalizeId( generate.source?.card_id ) ]?.name || 'record';
	const targetName = cardsById?.[ normalizeId( generate.create?.card_id ) ]?.name || 'item';
	const slots = Array.isArray( generate.occurrences?.slots ) ? generate.occurrences.slots : [];
	const perDay = slots.length
		? `${ slots.length } a day (${ joinAnd( slots.map( slot => slot.label ).filter( Boolean ) ) })`
		: 'occurrences';
	const days = Number( generate.horizon?.days );
	const ahead = Number.isFinite( days ) && days > 1 ? `, ${ days } days ahead` : '';
	return ` — making ${ perDay } ${ targetName } for each ${ sourceName }${ ahead }`;
}

/** What the schedule is checking FOR — the half that makes a cadence mean something. */
function describeCheck( check, cardsById ) {
	if ( ! check ) return '';
	const cardName = cardsById?.[ normalizeId( check.card_id ) ]?.name || 'item';
	const count = Number.isFinite( check.minCount ) ? check.minCount : 1;
	const window = check.window?.type === 'rolling'
		? ( Number.isFinite( check.window.minutes ) ? `in the last ${ check.window.minutes } minutes` : 'in a rolling window' )
		: ( check.window?.type === 'sinceSlot' ? 'since the previous check' : 'since midnight' );
	return ` — expecting ${ count } ${ cardName } ${ window }`;
}

const THRESHOLD_OPS = {
	lt: 'drops below',
	lte: 'drops to or below',
	gt: 'rises above',
	gte: 'reaches or rises above',
};

/** A measurement bound reads as it was authored; a number reads as itself. */
function boundText( bound ) {
	if ( bound?.kind === 'element' ) return bound.element || 'another value';
	const value = bound?.value;
	if ( value && typeof value === 'object' ) return `${ value.value ?? '' } ${ value.unit ?? '' }`.trim();
	return `${ value ?? '' }`;
}

/**
 * What a VALUE THRESHOLD watches, in a sentence: which number, which way, and
 * against what. The cadence is named only when there IS one — a threshold with
 * no sweep is a pure event rule, and saying "every day" about it would be
 * wrong in the direction a manager acts on.
 */
function describeThreshold( rule, cardsById ) {
	const watch = rule?.watch || {};
	const cardName = cardsById?.[ normalizeId( rule?.trigger?.card_id ) ]?.name;
	const subject = cardName ? `a ${ cardName }` : 'a record';
	const verb = THRESHOLD_OPS[ watch.op ] || 'crosses';
	let text = `When ${ subject }'s ${ watch.element || 'value' } ${ verb } ${ boundText( watch.bound ) }`;
	if ( watch.hysteresis ) text += ` (clearing by ${ watch.hysteresis.value }${ watch.hysteresis.kind === 'percent' ? '%' : '' })`;
	if ( watch.sweep ) text += ` — also checked ${ describeSchedule( watch.sweep ).replace( /^Every/, 'every' ) }`;
	const kql = rule?.trigger?.condition?.kql;
	if ( kql ) text += ` — if: ${ kql }`;
	return text;
}

/**
 * One rule's trigger, in a sentence. Pure and exported because it is the piece
 * of this tab most worth testing on its own — the rest of the row is antd.
 *
 * Deliberately reads the STORED shape rather than an editor draft: a rule
 * authored through the API (or a future importer) has to summarize just as well
 * as one built in the modal.
 */
export function describeTrigger( rule, cardsById = {}, tagsById = null, getElementsForCard = null ) {
	const trigger = rule?.trigger || {};
	if ( trigger.type === 'schedule' ) {
		if ( ! trigger.schedule ) return 'On a schedule';
		const what = rule?.kind === 'generate-items'
			? describeGeneration( rule?.generate, cardsById )
			: describeCheck( rule?.check, cardsById );
		return `${ describeSchedule( trigger.schedule ) }${ what }`;
	}
	// A rule that watches a repeating group's ENTRIES (KPR-687).
	// The group may be the card's own, inherited or from an element set: the
	// MERGED elements name it (cardsById holds the raw definitions).
	if ( trigger.type === ENTRY_EVENT ) {
		const cardId = normalizeId( trigger.card_id );
		const card = cardsById?.[ cardId ] || null;
		const elements = typeof getElementsForCard === 'function' ? getElementsForCard( cardId ) : null;
		return describeEntryTrigger( rule, card && Array.isArray( elements ) && elements.length ? { ...card, elements } : card );
	}
	// A card action has no event: a person presses it (KPR-382).
	if ( trigger.type === 'manual' ) return describeCardAction( rule, cardsById );
	if ( trigger.type !== 'item-event' ) return 'No trigger configured';
	// ⚠ A THRESHOLD IS AN ITEM-EVENT RULE BY TYPE, so this branch has to come
	// before the generic one or every threshold would summarize as "When a Part
	// is created or updated" — true, and useless, since what it actually
	// watches is in `watch`.
	if ( rule?.kind === 'threshold' ) return describeThreshold( rule, cardsById );
	const cardId = normalizeId( trigger.card_id );
	// An automatic tag's rule wakes on every write to its card (and its
	// related cards); what it DOES is the sentence worth reading.
	if ( rule?.kind === 'tag-rule' ) {
		const tagName = tagsById?.get?.( normalizeId( rule.tag_id ) )?.name;
		const card = cardsById?.[ normalizeId( rule.rule?.card_id ) || cardId ]?.name;
		return `Puts ${ tagName ? `“${ tagName }”` : 'its tag' } on ${ card ? `each ${ card }` : 'each item' } its rule holds for, and takes it off when the rule stops holding`;
	}
	// A card the definitions cache doesn't hold (still loading, or a card this
	// manager can't read) degrades to the generic noun rather than to a
	// dangling article.
	const cardName = cardsById?.[ cardId ]?.name;
	const subject = cardName ? `a ${ cardName }` : 'an item';
	const events = Array.isArray( trigger.events ) && trigger.events.length
		? trigger.events
		: [ 'created', 'updated', 'deleted' ];

	let text = `When ${ subject } is ${ joinOr( events ) }`;
	// Tagged / Untagged name their tags (T6a).
	const tagIds = Array.isArray( trigger.tagIds ) ? trigger.tagIds.map( normalizeId ).filter( Boolean ) : [];
	if ( tagIds.length && events.some( event => event === 'tagged' || event === 'untagged' ) ) {
		const names = tagIds.map( id => tagsById?.get?.( id )?.name ).filter( Boolean );
		const unnamed = tagIds.length - names.length;
		const words = [ ...names.map( name => `“${ name }”` ), ...( unnamed ? [ unnamed === 1 ? 'a tag' : `${ unnamed } tags` ] : [] ) ];
		text += ` (${ joinOr( words ) })`;
	}

	const changed = Array.isArray( trigger.elementsChanged ) ? trigger.elementsChanged : [];
	if ( changed.length ) text += `, and ${ joinOr( changed ) } changed`;

	const kql = trigger.condition?.kql;
	if ( kql ) text += ` — if: ${ kql }`;
	// A related-only condition must not read as unconditional in the list.
	if ( trigger.condition?.related?.length ) {
		text += kql ? ' (plus a cross-item condition)' : ' — with a cross-item condition';
	}

	return text;
}

const VIEW_PARAMS = {
	runsRule: { trim: false },
	runsPage: { default: 1, kind: 'page' },
};

/**
 * The open drill-down and its page live in the URL, per house convention
 * (hooks/useUrlParams, as useAttachmentsViewParams and useItemsViewParams): a
 * refresh, or a link pasted to another manager, lands back on the same run
 * history rather than on an empty tab. `replace` keeps the browser Back button
 * meaning "leave settings", not "undo my last click".
 */
function useAutomationsViewParams() {
	const { params: { runsRule, runsPage }, update } = useUrlParams( VIEW_PARAMS );
	return { runsRule, runsPage, update };
}

/** The truncation kinds recorded across one run's per-action results. */
const truncationsOf = run => {
	const results = Array.isArray( run?.results ) ? run.results : [];
	return Array.from( new Set( results.map( r => r?.truncated ).filter( Boolean ) ) );
};

function LastRunCell( { run } ) {
	if ( ! run ) return <Text type='secondary'>—</Text>;
	const status = run.status || 'unknown';
	const truncated = truncationsOf( run );
	return (
		<Space size={4} wrap>
			<Tag color={RUN_STATUS_COLORS[ status ] || 'default'} style={{ marginInlineEnd: 0 }}>{status}</Tag>
			<Text type='secondary'>{formatDisplayDateTime( run.startedAt )}</Text>
			{truncated.length ? (
				<Tag
					color='gold'
					style={{ marginInlineEnd: 0 }}
					title={`Stopped early: ${ truncated.map( kind => TRUNCATION_REASONS[ kind ] || kind ).join( '; ' ) }`}
				>
					stopped early
				</Tag>
			) : null}
		</Space>
	);
}

export default function AutomationsTab( { collectionId, collection, onWaitingCount } ) {

	const { message } = App.useApp();
	const navigate = useNavigate();
	const { runsRule, runsPage, update } = useAutomationsViewParams();
	// A waiting rule switched on from the list goes through the same
	// confirmation as the panel's Turn on: never on unseen (KPR-196 review F8).
	const [ confirmingOn, setConfirmingOn ] = useState( null );

	const [ rules, setRules ] = useState( [] );
	// A sub-collection's parent's rules, which run over it too (ruling D7).
	const [ inheritedRules, setInheritedRules ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ loadError, setLoadError ] = useState( null );
	// The first read has answered: until then the tab is a skeleton, after it the tab keeps its data.
	const [ loaded, setLoaded ] = useState( false );
	const [ lastRuns, setLastRuns ] = useState( {} );
	const [ editing, setEditing ] = useState( null ); // null = closed; {} = new rule
	// Reminders are a different KIND with a different editor (schedule + check +
	// notify, no item event anywhere in it), so they get their own slot rather
	// than a mode flag on one overloaded modal.
	const [ editingReminder, setEditingReminder ] = useState( null );
	// And a third kind with a third editor: a generator asks what to make, not
	// what to watch for. Same reasoning as the reminder's own slot — one modal
	// carrying three unrelated shapes would be a mode flag pretending to be a
	// form.
	const [ editingGenerator, setEditingGenerator ] = useState( null );
	// And a fourth: a threshold asks about a NUMBER crossing a line, which has
	// no field in common with the event rule's checkbox row.
	const [ editingThreshold, setEditingThreshold ] = useState( null );
	// And a fifth: a card action is a button people press, not a rule that
	// watches — its editor asks for a label, a condition and what is asked.
	const [ editingAction, setEditingAction ] = useState( null );

	// Run timestamps render through the shared display formatter; subscribe so a
	// preference change re-renders these cells live.
	useDateFormat();

	// Optional on purpose: the tab still renders (name, kind, enabled) without
	// the definitions cache — only the card NAMES in the summaries degrade.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo( () => cardDefinitions?.cardsById || {}, [ cardDefinitions ] );
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	// Tag names (T6a): an automatic tag's rule is named from its tag (the
	// rule's own stored name keeps the tag's old name after a rename), and a
	// Tagged / Untagged trigger names its tags. Asked for only when a rule
	// here needs them.
	const namesTags = [ ...rules, ...inheritedRules ].some( rule => rule?.kind === 'tag-rule'
		|| ( Array.isArray( rule?.trigger?.tagIds ) && rule.trigger.tagIds.length ) );
	const { tags: collectionTags } = useCollectionTags( collectionId, { enabled: Boolean( collectionId && namesTags ) } );
	const tagIndex = useMemo( () => indexTags( collectionTags ), [ collectionTags ] );
	const ruleName = rule => {
		if ( rule?.kind === 'tag-rule' ) {
			const tag = tagIndex.get( normalizeId( rule.tag_id ) );
			if ( tag?.name ) return `Automatic tag: ${ tag.name }`;
		}
		return rule?.name || 'Untitled rule';
	};

	// The cards a rule may reference: this collection's members, two-source like
	// everywhere else (embedded refs + cards owned by the collection).
	const memberCardIds = useMemo(
		() => getCollectionMemberCardIds( collection, Object.values( cardsById ) ),
		[ collection, cardsById ]
	);
	const collectionCards = useMemo(
		() => Object.values( cardsById ).filter( card => memberCardIds.has( normalizeId( card?._id ) ) ),
		[ cardsById, memberCardIds ]
	);

	const reload = useCallback( () => {
		if ( ! collectionId ) return Promise.resolve();
		// `describe`: keepr's sentence for each rule, which the waiting rules
		// read (KPR-196).
		return getAutomations( collectionId, { includeInherited: true, describe: true } )
			.then( data => {
				const all = Array.isArray( data ) ? data : [];
				const list = all.filter( rule => ! rule?.inherited );
				setRules( list );
				if ( onWaitingCount ) onWaitingCount( list.filter( rule => rule?.awaitingPerson && rule?.enabled === false ).length );
				setInheritedRules( all.filter( rule => rule?.inherited ) );
				setLoadError( null );
				setLoaded( true );
				return list;
			} )
			.catch( error => {
				setLoadError( error );
				return [];
			} );
	}, [ collectionId, onWaitingCount ] );

	useEffect( () => {
		setLoading( true );
		reload().finally( () => setLoading( false ) );
	}, [ reload ] );

	// Keyed on the rule IDS, not on the rules array: an optimistic enable/disable
	// replaces that array, and re-probing every rule's ledger because one switch
	// moved would be a fan-out of requests for a column that did not change.
	const ruleIdsKey = useMemo(
		() => rules.map( rule => normalizeId( rule?._id ) ).filter( Boolean ).join( ',' ),
		[ rules ]
	);

	// One last-run probe per rule, in parallel. `allSettled` because a single
	// rule's ledger failing must not blank the column for every other row — a
	// rejected probe simply renders as "no runs yet".
	useEffect( () => {
		if ( ! collectionId || ! ruleIdsKey ) return undefined;
		let active = true;
		const ids = ruleIdsKey.split( ',' );
		Promise.allSettled(
			ids.map( id => getAutomationRuns( collectionId, id, { limit: 1 } ) )
		).then( results => {
			if ( ! active ) return;
			const next = {};
			results.forEach( ( result, index ) => {
				const rows = result.status === 'fulfilled' && Array.isArray( result.value ) ? result.value : [];
				next[ ids[ index ] ] = rows[ 0 ] || null;
			} );
			setLastRuns( next );
		} );
		return () => { active = false; };
	}, [ collectionId, ruleIdsKey ] );

	const handleToggle = useCallback( ( rule, next ) => {
		if ( next && rule?.awaitingPerson ) { setConfirmingOn( rule ); return; }
		const id = normalizeId( rule?._id );
		// Optimistic: the switch answers instantly and rolls back if the API
		// refuses — the house pattern for every one-field toggle in the app.
		const apply = value => setRules( prev => prev.map(
			row => ( normalizeId( row?._id ) === id ? { ...row, enabled: value } : row )
		) );
		apply( next );
		( next ? enableAutomation : disableAutomation )( collectionId, id ).then( () => {
			// An automatic tag's rule: the tag tree says Paused from the
			// vocabulary's `rule.enabled` (keepr-api T6b), so it is read again.
			if ( rule?.kind === 'tag-rule' ) invalidateTagOwner( collectionId );
			// Turned on by the switch, a waiting rule waits no longer: read again
			// so the panel and the tab's count say so (KPR-196).
			if ( next && rule?.awaitingPerson ) reload();
		}, err => {
			apply( ! next );
			message.error( err?.response?.data?.message || 'Unable to change this rule right now. Please try again.' );
		} );
	}, [ collectionId, message, reload ] );

	const handleDelete = useCallback( ( rule ) => {
		const id = normalizeId( rule?._id );
		const action = rule?.kind === 'card-action';
		confirmDestructive( Modal, {
			title: action ? `Delete the "${ rule?.name || 'Untitled action' }" action?` : `Delete the "${ rule?.name || 'Untitled rule' }" rule?`,
			content: action
				? 'The button disappears from every item. Data it already wrote stays as it is. History can restore it.'
				: 'It stops running immediately. Data it already wrote stays as it is. History can restore it.',
			okText: 'Delete',
			onOk: async () => {
				try {
					await deleteAutomation( collectionId, id );
					message.success( 'Rule deleted.' );
					await reload();
				}
				catch ( err ) {
					message.error( err?.response?.data?.message || 'Delete failed.' );
				}
			},
		} );
	}, [ collectionId, message, reload ] );

	// Review a waiting rule: its own editor, as Edit opens it — an automatic
	// tag's rule on its tag, where it is configured.
	const openEditor = useCallback( ( rule ) => {
		const kind = rule?.kind || 'rule';
		if ( kind === 'tag-rule' ) navigate( `/collections/${ collectionId }/form?tab=tags&rule=${ normalizeId( rule?.tag_id ) }` );
		else if ( kind === 'expected-item' ) setEditingReminder( rule );
		else if ( kind === 'generate-items' ) setEditingGenerator( rule );
		else if ( kind === 'threshold' ) setEditingThreshold( rule );
		else if ( kind === 'card-action' ) setEditingAction( rule );
		else setEditing( rule );
	}, [ collectionId, navigate ] );

	// Turned on from the panel or the switch's confirmation: read again, and an
	// automatic tag's rule moves its tag's Paused too.
	const afterTurnedOn = useCallback( ( rule ) => {
		if ( rule?.kind === 'tag-rule' ) invalidateTagOwner( collectionId );
		reload();
	}, [ collectionId, reload ] );

	const openRule = useMemo(
		() => rules.find( rule => normalizeId( rule?._id ) === runsRule ) || null,
		[ rules, runsRule ]
	);

	const retryLoad = () => {
		setLoading( true );
		reload().finally( () => setLoading( false ) );
	};
	if ( ! loaded ) {
		return (
			<TabLoadState
				loading={loading}
				error={loadError}
				label='automations'
				errorDetail='Automations are manager-only. If your access just changed, that may be why.'
				onRetry={retryLoad}
			/>
		);
	}

	return (
		<div className='automations-tab'>
			{loadError ? <RefreshFailedNotice label='automations' onRetry={retryLoad} /> : null}

			<Alert
				type='info'
				showIcon
				className='sharing-tab__notice'
				message='Changes on this tab apply immediately'
				description="Unlike the other settings, automation changes don't wait for the Save button."
			/>

			{/* What an assistant made, waiting for the person (KPR-196): first,
			    because it is the thing to do here. */}
			<WaitingRules collectionId={collectionId} rules={rules} nameOf={ruleName} onReview={openEditor} onChanged={afterTurnedOn} />

			{ confirmingOn ? (
				<TurnOnRuleModal
					collectionId={collectionId}
					rule={confirmingOn}
					nameOf={ruleName}
					onClose={() => setConfirmingOn( null )}
					onDone={() => { const rule = confirmingOn; setConfirmingOn( null ); afterTurnedOn( rule ); }}
				/>
			) : null }

			{/* A parent's rules run over its whole family (ruling D7): said
			    once, plainly. */}
			{ subCollectionsOf( collection ).length ? (
				<p className='automations-tab__family-note meta-line' data-testid='automations-family-note'>
					{ subCollectionsOf( collection ).length === 1
						? 'Rules here also run over its sub-collection.'
						: `Rules here also run over its ${ subCollectionsOf( collection ).length } sub-collections.` }
				</p>
			) : null }

			<div style={{ display: 'flex', justifyContent: 'flex-end', margin: '12px 0' }}>
				<Space wrap>
					<Button icon={<PLUS />} onClick={() => setEditingAction( {} )}>New action</Button>
					<Button icon={<PLUS />} onClick={() => setEditingThreshold( {} )}>New threshold</Button>
					<Button icon={<PLUS />} onClick={() => setEditingGenerator( {} )}>New generator</Button>
					<Button icon={<PLUS />} onClick={() => setEditingReminder( {} )}>New reminder</Button>
					<Button type='primary' icon={<PLUS />} onClick={() => setEditing( {} )}>New rule</Button>
				</Space>
			</div>

			<List
				bordered
				dataSource={rules}
				locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={inheritedRules.length ? 'Nothing is defined here yet' : 'No automations yet'} /> }}
				rowKey={rule => normalizeId( rule?._id )}
				renderItem={rule => {
					const id = normalizeId( rule?._id );
					const kind = rule?.kind || 'rule';
					const managed = !! rule?.managed;
					const sourceCardId = normalizeId( rule?.source?.cardDefinitionId );
					const sourceCardName = cardsById?.[ sourceCardId ]?.name || 'its card';
					return (
						<List.Item key={id} style={{ display: 'block' }}>
							<Space direction='vertical' size={4} style={{ width: '100%' }}>

								<Space size={8} wrap>
									<Text strong>{ruleName( rule )}</Text>
									<Tag color={KIND_COLORS[ kind ] || 'default'} style={{ marginInlineEnd: 0 }}>
										{KIND_LABELS[ kind ] || kind}
									</Tag>
									{ rule?.awaitingPerson && rule?.enabled === false ? <Tag style={{ marginInlineEnd: 0 }}>Waiting for you</Tag> : null }
									{ rule?.alwaysRunOnCardChange === true ? <Tag style={{ marginInlineEnd: 0 }}>{ALWAYS_RUN_LABEL}</Tag> : null }
								</Space>

								<Text type='secondary'>{describeTrigger( rule, cardsById, tagIndex, getElementsForCard )}</Text>

								<Space size={12} wrap>
									<Switch
										size='small'
										checked={rule?.enabled !== false}
										onChange={next => handleToggle( rule, next )}
										aria-label={`${ ruleName( rule ) } enabled`}
									/>
									<LastRunCell run={lastRuns[ id ]} />
									<Button
										type='link'
										size='small'
										style={{ padding: 0 }}
										onClick={() => update( { runsRule: id, runsPage: 1 } )}
									>
										runs
									</Button>
									{managed && kind === 'tag-rule' ? (
										// An automatic tag's rule is authored on its tag (T6a):
										// the door is Settings › Tags with its editor open.
										<Link to={`/collections/${ collectionId }/form?tab=tags&rule=${ normalizeId( rule?.tag_id ) }`}>Configured on its tag</Link>
									) : managed ? (
										// Managed rules have exactly one authoring surface — the
										// driven element on its card. A second editor here would
										// fight the server-side materializer.
										<Link to={`/cards/${ sourceCardId }`}>{`Configured on ${ sourceCardName }`}</Link>
									) : (<>
										<Button
											size='small'
											onClick={() => openEditor( rule )}
										>
											Edit
										</Button>
										<Button size='small' danger onClick={() => handleDelete( rule )}>Delete</Button>
									</>)}
								</Space>

							</Space>
						</List.Item>
					);
				}}
			/>

			<InheritedRules
				rules={inheritedRules}
				cardsById={cardsById}
				managesParent={managesParentOf( collection )}
				tab='automations'
				kindLabel={rule => KIND_LABELS[ rule?.kind || 'rule' ] || rule?.kind}
				describe={rule => describeTrigger( rule, cardsById, tagIndex, getElementsForCard )}
			/>

			{editing ? (
				<AutomationRuleEditor
					open
					collectionId={collectionId}
					rule={editing._id ? editing : null}
					collectionCards={collectionCards}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					onClose={() => setEditing( null )}
					onSaved={() => { setEditing( null ); reload(); }}
				/>
			) : null}

			{editingReminder ? (
				<ExpectedItemRuleEditor
					open
					collectionId={collectionId}
					rule={editingReminder._id ? editingReminder : null}
					collectionCards={collectionCards}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					onClose={() => setEditingReminder( null )}
					onSaved={() => { setEditingReminder( null ); reload(); }}
				/>
			) : null}

			{editingGenerator ? (
				<GenerateItemsRuleEditor
					open
					collectionId={collectionId}
					rule={editingGenerator._id ? editingGenerator : null}
					collectionCards={collectionCards}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					onClose={() => setEditingGenerator( null )}
					onSaved={() => { setEditingGenerator( null ); reload(); }}
				/>
			) : null}

			{editingThreshold ? (
				<ThresholdRuleEditor
					open
					collectionId={collectionId}
					rule={editingThreshold._id ? editingThreshold : null}
					collectionCards={collectionCards}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					onClose={() => setEditingThreshold( null )}
					onSaved={() => { setEditingThreshold( null ); reload(); }}
				/>
			) : null}

			{editingAction ? (
				<CardActionEditor
					open
					collectionId={collectionId}
					rule={editingAction._id ? editingAction : null}
					collectionCards={collectionCards}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					onClose={() => setEditingAction( null )}
					onSaved={() => { setEditingAction( null ); reload(); }}
				/>
			) : null}

			{runsRule ? (
				<AutomationRunsModal
					open
					collectionId={collectionId}
					ruleId={runsRule}
					ruleName={openRule?.name}
					page={runsPage}
					onPage={page => update( { runsPage: page } )}
					onClose={() => update( { runsRule: undefined, runsPage: 1 } )}
				/>
			) : null}

		</div>
	);
}

/**
 * "Inherited from Biewend": the rules defined on the collections above a
 * sub-collection that run over it too (docs/plans/sub-collections.md §4.7),
 * one group per collection, read-only — each changed where it is defined,
 * behind a door for whoever manages that collection. Shared with the
 * Notifications tab: `describe(rule)` and `kindLabel(rule)` say what a row is,
 * `tab` names the settings tab the door opens.
 */
export function InheritedRules( { rules = [], managesParent = false, tab, describe, kindLabel } ) {
	if ( ! rules.length ) return null;
	const byCollection = new Map();
	rules.forEach( rule => {
		const id = normalizeId( rule?.inheritedFrom?._id );
		if ( ! id ) return;
		if ( ! byCollection.has( id ) ) byCollection.set( id, { from: rule.inheritedFrom, rows: [] } );
		byCollection.get( id ).rows.push( rule );
	} );
	return [ ...byCollection.entries() ].map( ( [ id, { from, rows } ] ) => (
		<div key={id} className='inherited-rules' data-testid='inherited-rules'>
			<GroupHeader as='h4' label={`Inherited from ${ from?.name || 'the parent collection' }`} count={rows.length} />
			<List
				bordered
				dataSource={rows}
				rowKey={rule => normalizeId( rule?._id )}
				renderItem={rule => {
					const kind = kindLabel ? kindLabel( rule ) : null;
					const summary = describe ? describe( rule ) : null;
					return (
						<List.Item key={normalizeId( rule?._id )} style={{ display: 'block' }}>
							<Space direction='vertical' size={4} style={{ width: '100%' }}>
								<Space size={8} wrap>
									<Text strong>{rule?.name || 'Untitled rule'}</Text>
									{ kind ? <Tag style={{ marginInlineEnd: 0 }}>{kind}</Tag> : null }
									{ rule?.enabled === false ? <Tag style={{ marginInlineEnd: 0 }}>Paused</Tag> : null }
									{ rule?.alwaysRunOnCardChange === true ? <Tag style={{ marginInlineEnd: 0 }}>{ALWAYS_RUN_LABEL}</Tag> : null }
								</Space>
								{ summary ? <Text type='secondary'>{summary}</Text> : null }
							</Space>
						</List.Item>
					);
				}}
			/>
			<p className='inherited-rules__note meta-line'>
				{ managesParent
					? <>{`Defined on ${ from?.name }, these run over this collection too. `}<Link to={`/collections/${ id }/form?tab=${ tab }`}>{`Change on ${ from?.name }`}</Link></>
					: `Defined on ${ from?.name }, these run over this collection too. Someone who manages ${ from?.name } can change them there.` }
			</p>
		</div>
	) );
}
